/**
 * Renders type-specific icon and accent color for notifications.
 */

import type { NotificationType } from '@/types/notifications';
import { calculateDaysRemaining } from '@/utils/formatters';

interface NotificationTypeIconProps {
  type: NotificationType;
  symbol?: string;
  expirationDate?: string;
}

const TYPE_CONFIG: Record<NotificationType, { emoji: string; accentClass: string }> = {
  trade_reminder: { emoji: '📊', accentClass: 'border-l-blue-500' },
  wash_sale_alert: { emoji: '⚠️', accentClass: 'border-l-rose-500' },
  portfolio_review: { emoji: '📋', accentClass: 'border-l-purple-500' },
  system_message: { emoji: '🔔', accentClass: 'border-l-dark-400' },
};

export function getAccentClass(type: NotificationType): string {
  return TYPE_CONFIG[type]?.accentClass ?? TYPE_CONFIG.system_message.accentClass;
}

export function NotificationTypeIcon({ type, symbol, expirationDate }: NotificationTypeIconProps) {
  const config = TYPE_CONFIG[type] ?? TYPE_CONFIG.system_message;

  return (
    <div className="flex items-center gap-2">
      <span className="text-base" aria-hidden="true">
        {config.emoji}
      </span>
      {symbol && (
        <span className="rounded bg-dark-700 px-1.5 py-0.5 text-xs font-mono font-medium text-dark-200 uppercase">
          {symbol.slice(0, 5)}
        </span>
      )}
      {type === 'wash_sale_alert' && expirationDate && (
        <ExpirationCountdown date={expirationDate} />
      )}
    </div>
  );
}

function ExpirationCountdown({ date }: { date: string }) {
  const days = calculateDaysRemaining(date);

  if (days <= 0) {
    return (
      <span className="text-xs font-medium text-rose-400">Expired</span>
    );
  }

  return (
    <span className="text-xs text-dark-400">
      {days}d left
    </span>
  );
}
